import { escapeHtml } from '@helpers';
import { SalesStrategySection } from '@apiHooks/useSalesStrategy';
import {
    COPY_CELL_STYLE,
    COPY_CONTAINER_STYLE,
    COPY_HEADING_STYLE,
    COPY_SOURCE_ID,
    COPY_SOURCE_STYLE,
    COPY_TABLE_STYLE,
    COPY_TH_STYLE,
    STRIPPED_ATTRIBUTES,
    STRIPPED_TAGS,
    STRIPPED_URL_SCHEMES,
    URL_ATTRIBUTES,
} from './StrategyCopyButton.constants';
import { formatReferenceLinks, toTitleCase } from '../Strategy.helpers';
import { REFERENCES_SECTION_ID, SSB_SELLING_FOCUS_TITLE } from '../Strategy.constants';

function stripDisallowedTags(root: Element): void {
    STRIPPED_TAGS.forEach(tag => {
        root.querySelectorAll(tag).forEach(el => el.remove());
    });
}

function isDisallowedUrlScheme(value: string): boolean {
    const anchor = document.createElement('a');
    anchor.href = value;
    const scheme = anchor.protocol.replace(':', '').toLowerCase();

    return STRIPPED_URL_SCHEMES.includes(scheme);
}

function stripDisallowedUrlAttributes(el: Element): void {
    URL_ATTRIBUTES.forEach(attribute => {
        const value = el.getAttribute(attribute);

        if (value && isDisallowedUrlScheme(value)) {
            el.removeAttribute(attribute);
        }
    });
}

function stripDisallowedAttributes(root: Element): void {
    const elements = [root, ...Array.from(root.querySelectorAll('*'))];

    elements.forEach(el => {
        STRIPPED_ATTRIBUTES.forEach(attribute => el.removeAttribute(attribute));

        Array.from(el.attributes)
            .filter(attribute => attribute.name.startsWith('data-') || attribute.name.toLowerCase().startsWith('on'))
            .forEach(attribute => el.removeAttribute(attribute.name));

        stripDisallowedUrlAttributes(el);
    });
}

function applyTableStyles(root: Element): void {
    root.querySelectorAll('table').forEach(el => el.setAttribute('style', COPY_TABLE_STYLE));
    root.querySelectorAll('th').forEach(el => el.setAttribute('style', COPY_TH_STYLE));
    root.querySelectorAll('td').forEach(el => el.setAttribute('style', COPY_CELL_STYLE));
}

function sanitizeStrategyHtml(rawHtml: string): string {
    const div = document.createElement('div');
    div.innerHTML = rawHtml;

    stripDisallowedTags(div);
    stripDisallowedAttributes(div);
    applyTableStyles(div);

    return div.innerHTML;
}

function buildSellingFocusBlock(userInput: string | null): string {
    if (userInput === null || userInput.trim() === '') {
        return '';
    }

    return `<h2 style="${COPY_HEADING_STYLE}">${SSB_SELLING_FOCUS_TITLE}</h2><p>${escapeHtml(userInput)}</p>`;
}

function buildSectionBody(section: SalesStrategySection): string {
    const html = section.html ?? '';

    if (section.id === REFERENCES_SECTION_ID) {
        return sanitizeStrategyHtml(formatReferenceLinks(html));
    }

    return sanitizeStrategyHtml(html);
}

function buildSectionBlock(section: SalesStrategySection): string {
    const heading = section.title && section.title.trim() !== '' ? `<h2 style="${COPY_HEADING_STYLE}">${escapeHtml(toTitleCase(section.title))}</h2>` : '';

    return heading + buildSectionBody(section);
}

export function buildStrategyCopyHtml(sections: SalesStrategySection[], userInput: string | null): string {
    const sellingFocusBlock = buildSellingFocusBlock(userInput);
    const sectionsHtml = sections.map(buildSectionBlock).join('');

    if (sellingFocusBlock === '' && sectionsHtml === '') {
        return '';
    }

    return `<div style="${COPY_CONTAINER_STYLE}">${sellingFocusBlock}${sectionsHtml}</div>`;
}

export function createCopyContainer(html: string): HTMLElement {
    removeCopyContainer();

    const container = document.createElement('div');
    container.id = COPY_SOURCE_ID;
    container.setAttribute('style', COPY_SOURCE_STYLE);
    container.setAttribute('aria-hidden', 'true');
    container.innerHTML = html;
    document.body.appendChild(container);

    return container;
}

export function removeCopyContainer(): void {
    document.getElementById(COPY_SOURCE_ID)?.remove();
}
